import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Modal, Tabs } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react';
import { isEmpty } from 'lodash';
import cn from 'classnames';

import { permissions } from '@/constants';
import { useStores } from '@/hooks/use-stores';
import { useMeasurement } from '@/hooks/useMeasurement';
import { ENGINE_VERSION_BASE, ENGINE_VERSION_BASE_MIN } from '@/store/Engine/constants';
import EcoSelectStation from '@/comp/EcoSelectStation/EcoSelectStation';
import { EPBetaTag } from '@/comp/common/EPTag';
import ConditionalToolTip from '@/utils/ConditionalTooltip';
import { formatEngineConfigDate } from '@/utils/util-functions';

import { EnginePageContainer } from './styled';
import EngineVersion from './EngineVersion';
import { ValidationMessagePopoverGlobalStyle } from './Engine/Common/styled';
import VersionHistory from './VersionHistory/VersionHistory';
import PIDChangesModal from './PIDChangesModal/PIDChangesModal';
import Wizard from './Engine/Wizard/Wizard';
import { EPButton } from '@/components';
import StationStages from '@/comp/StationStages/StationStages';
import {
  getTabTitleEnginePage,
  handleEngineSupport,
  handleNoPermissionCase,
  handleTabChangeInEnginePage,
  handleTabConfig,
  initializeEngineConfig,
  setInitialTab,
} from './EnginePage.utils';
import { versionTabs } from './Engine/CentrifugalCompressors/CentrifugalCompressors.utils';

const EnginePage = observer(() => {
  const { permissionStore, engineStore, stationStore, devicesStore } = useStores();
  const navigate = useNavigate();
  const { tvalue } = useMeasurement();
  const { stationId } = useParams();

  const [activeTab, setActiveTab] = useState(null);
  const [tabTitleText, setTabTitleText] = useState('');
  const [hasTriedToSubmit, setHasTriedToSubmit] = useState(false);
  const [confirmationModalInfo, setConfirmationModalInfo] = useState({
    open: false,
    modalText: undefined,
    actionHandler: undefined,
  });
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const [infoModalText, setInfoModalText] = useState('');
  const hasViewPermission = permissionStore.isAuth(permissions.VIEW_STATION_ENGINE);
  const hasUpdatePermission = permissionStore.isAuth(permissions.UPDATE_STATION_ENGINE);

  const fetchDeviceData = async () => {
    await devicesStore.getDevicesMetadata();
  };

  const syncKpiChanges = async () => {
      await engineStore.syncKpiChanges();
  };
  useEffect(() => {
    const fetchDeviceData = async () => {
      await devicesStore.getDevicesMetadata();
    };
    if (engineStore.hasPIDChanges) {
      fetchDeviceData();
    }
  }, [engineStore.hasPIDChanges]);

  useEffect(() => {
    if(engineStore.isMixedStation){
      setInfoModalOpen(true);
      setInfoModalText(t('mixedStationNotSupported'));
    }
  }, [stationStore.currentStation?.id, engineStore.isMixedStation]);

  useEffect(() => {
    const setupEngine = async () => {
      if (!hasViewPermission) {
        navigate('/home');
        return;
      }
      await initializeEngineConfig(engineStore, tvalue);

      if (!handleNoPermissionCase(hasViewPermission, engineStore.publishedEngineConfig, setInfoModalOpen)) {
        return;
      }
      setInitialTab(engineStore, tvalue, setActiveTab);
    };
    setupEngine();
  }, [stationStore.currentStation?.id, hasViewPermission, hasUpdatePermission]);

  useEffect(() => {
    const fetchEngineSupport = async () => {
      try {
        await engineStore.getEngineSupport();
        handleEngineSupport(engineStore, stationStore.currentStation?.id, navigate);
      } catch (error) {
        console.error('Error fetching engine support:', error);
      }
    };

    fetchEngineSupport();
    fetchDeviceData();
    syncKpiChanges();
  }, [stationStore.currentStation?.id, engineStore, navigate]);

  useEffect(() => {
    handleTabConfig(activeTab, engineStore, tvalue);
  }, [activeTab]);

  const onTabChange = async tab => {
    await handleTabChangeInEnginePage(tab, engineStore, tvalue, hasViewPermission, hasUpdatePermission, setActiveTab);
  };

  const closeInfoModal = () => {
    setInfoModalOpen(false);
    navigate(`/home/station/${stationId}`);
  };

  const TabTitle = observer(({ engineConfigObj }) => {
    const tabTitle = getTabTitleEnginePage(engineConfigObj, engineStore.publishedEngineConfig);

    useEffect(() => {
      setTabTitleText(tabTitle);
    }, [tabTitle]);

    const isDisabled = !hasViewPermission && activeTab !== 'published';
    const showVersion = tabTitle !== 'createNewDraft' && tabTitle !== 'recommended';
    const version = engineConfigObj?.version ?? ENGINE_VERSION_BASE_MIN;

    return (
      <span className={isDisabled ? 'disabled-tab' : undefined}>
        <span className={cn(!hasViewPermission ? 'disabled' : undefined)}>{t(tabTitle)}</span>
        {showVersion && (
          <span className={!hasViewPermission ? 'disabled' : undefined}>
            &nbsp;({ENGINE_VERSION_BASE}
            {version})
          </span>
        )}
      </span>
    );
  });

  const tabItems = [
    ...(engineStore.hasFinishedWizard
      ? [
          ...(!isEmpty(engineStore.publishedEngineConfig)
            ? [
                {
                  label: <TabTitle engineConfigObj={engineStore.publishedEngineConfig} />,
                  key: versionTabs.published,
                  children: (
                    <EngineVersion
                      setParentActiveTab={setActiveTab}
                      parentActiveTab={activeTab}
                      hasTriedToSubmit={hasTriedToSubmit}
                      setHasTriedToSubmit={setHasTriedToSubmit}
                      confirmationModalInfo={confirmationModalInfo}
                      setConfirmationModalInfo={setConfirmationModalInfo}
                    />
                  ),
                },
                {
                  label: <TabTitle engineConfigObj={engineStore.draftEngineConfig} />,
                  key: versionTabs.draft,
                  disabled: !hasUpdatePermission && tabTitleText === 'createNewDraft',

                  children: (
                    <EngineVersion
                      setParentActiveTab={setActiveTab}
                      parentActiveTab={activeTab}
                      hasTriedToSubmit={hasTriedToSubmit}
                      setHasTriedToSubmit={setHasTriedToSubmit}
                      confirmationModalInfo={confirmationModalInfo}
                      setConfirmationModalInfo={setConfirmationModalInfo}
                    />
                  ),
                },
                {
                  label: <span className={cn(!hasViewPermission && 'disabled-tab')}>{t('versionHistory')}</span>,
                  key: versionTabs.versionHistory,
                  children: <VersionHistory />,
                },
              ]
            : [
                {
                  label: <TabTitle engineConfigObj={engineStore.draftEngineConfig} />,
                  key: versionTabs.draft,
                  children: (
                    <EngineVersion
                      setParentActiveTab={setActiveTab}
                      parentActiveTab={activeTab}
                      hasTriedToSubmit={hasTriedToSubmit}
                      setHasTriedToSubmit={setHasTriedToSubmit}
                      confirmationModalInfo={confirmationModalInfo}
                      setConfirmationModalInfo={setConfirmationModalInfo}
                    />
                  ),
                },
              ]),
        ]
      : [
          {
            label: t('wizard'),
            key: versionTabs.engine,
            children: (
              <Wizard
                setActiveTab={setActiveTab}
                hasTriedToSubmit={hasTriedToSubmit}
                setHasTriedToSubmit={setHasTriedToSubmit}
                confirmationModalInfo={!engineStore.hasFinishedWizard ? confirmationModalInfo : null}
                setConfirmationModalInfo={setConfirmationModalInfo}
              />
            ),
          },
        ]),
    ...(!isEmpty(engineStore.automaticEngineConfig)
      ? [
          {
            label: (
              <ConditionalToolTip
                condition={!window.localStorage.getItem('hasVisitedRecommendedTab')}
                content={t('newRecommendedConfiguration', {
                  createdDate: formatEngineConfigDate(engineStore.automaticEngineConfig?.createdDate),
                })}
                placement="top"
              >
                <div className={'recommendation-tab'}>
                  <TabTitle engineConfigObj={engineStore.automaticEngineConfig} />
                  {!window.localStorage.getItem('hasVisitedRecommendedTab') && (
                    <span className="new-recommendation-indication">*</span>
                  )}
                  <EPBetaTag className="engine-beta-tag" />
                </div>
              </ConditionalToolTip>
            ),
            key: versionTabs.recommendation,
            children: (
              <EngineVersion
                setParentActiveTab={setActiveTab}
                parentActiveTab={activeTab}
                hasTriedToSubmit={hasTriedToSubmit}
                setHasTriedToSubmit={setHasTriedToSubmit}
                confirmationModalInfo={confirmationModalInfo}
                setConfirmationModalInfo={setConfirmationModalInfo}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <EnginePageContainer $activeTab={activeTab} $hasUpdatePermission={hasViewPermission}>
      <ValidationMessagePopoverGlobalStyle />
      <div className="engine-header-container">
        <EcoSelectStation requiredPermission={permissions.VIEW_STATION_ENGINE} />
        <StationStages />
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        animated={{
          inkBar: true,
          tabPane: false,
        }}
        items={tabItems}
        destroyInactiveTabPane
        moreIcon={false}
      />
      <Modal
        open={isInfoModalOpen}
        onOk={closeInfoModal}
        okText={t('ok')}
        centered
        maskClosable={false}
        closable={false}
        footer={[
          <EPButton key="admin-modal" type="primary" onClick={closeInfoModal} size="medium">
            {t('ok')}
          </EPButton>,
        ]}
      >
        <span>{infoModalText}</span>
      </Modal>
      <PIDChangesModal />
    </EnginePageContainer>
  );
});

export default EnginePage;
